/**
 * SwitchPrintTechnologyDialog (BLU-1412, in place since BLU-1509)
 *
 * Confirms switching one garment between DTF and DTG before anything is
 * printed. The switch is in place: the garment keeps its status, its label and
 * its place in the batch, and only its print technology changes. No preflight
 * read: the server re-checks everything and names the reason when it refuses,
 * which this dialog shows inline so the operator can act on it.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { AlertTriangle, ArrowRight, Info, XCircle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import type { PrintTechnology } from '@/features/production-orders/utils/print-tech-routing';
import { useSwitchGarmentPrintTechnology } from '../hooks/useStatusOverride';
import {
  SWITCH_REFUSAL_KEYS,
  hasPrintedLabel,
  type PrintTechnologySwitchRefusal,
} from '../lib/print-technology-switch';

export interface SwitchPrintTechnologyTarget {
  garmentId: string;
  sku: string;
  productName: string;
  status: string;
  from: PrintTechnology;
  to: PrintTechnology;
  locationId: string;
  /** The expanded order and the garment's batch: the only views refreshed after. */
  orderId?: string;
  batchId?: string | null;
}

interface SwitchPrintTechnologyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  target: SwitchPrintTechnologyTarget;
}

const NOTE_MAX = 500;

export function SwitchPrintTechnologyDialog({ open, onOpenChange, target }: SwitchPrintTechnologyDialogProps) {
  const { t } = useTranslation('production');
  const [note, setNote] = useState('');
  const [refusal, setRefusal] = useState<PrintTechnologySwitchRefusal | null>(null);
  const mutation = useSwitchGarmentPrintTechnology({ orderId: target.orderId, batchId: target.batchId });
  const isPending = mutation.isPending;

  useEffect(() => {
    if (!open) {
      setNote('');
      setRefusal(null);
    }
  }, [open]);

  const handleConfirm = async () => {
    setRefusal(null);
    try {
      const result = await mutation.mutateAsync({
        garmentUnitId: target.garmentId,
        locationId: target.locationId,
        target: target.to,
        note: note.trim() || undefined,
      });
      if (!result.ok) {
        setRefusal(result.reason);
        return;
      }
      toast.success(t('tracker.printTechSwitch.done', { tech: target.to }), {
        description: hasPrintedLabel(result.status)
          ? t('tracker.printTechSwitch.labelStillSays', { tech: target.from })
          : undefined,
      });
      onOpenChange(false);
    } catch (error) {
      toast.error(t('tracker.printTechSwitch.failed'), {
        description: error instanceof Error ? error.message : undefined,
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!isPending) onOpenChange(v); }}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader className="text-center sm:text-center">
          <DialogTitle>{t('tracker.printTechSwitch.title', { tech: target.to })}</DialogTitle>
          <DialogDescription>
            {target.productName} · <span className="font-mono">{target.sku}</span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex items-center justify-center gap-3 py-2">
            <Badge variant="outline" className="text-xs">{target.from}</Badge>
            <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
            <Badge variant="outline" className="text-xs">{target.to}</Badge>
          </div>

          <p className="text-sm">{t('tracker.printTechSwitch.body', { tech: target.to })}</p>

          {hasPrintedLabel(target.status) && (
            <div className="flex items-start gap-2 rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800 dark:border-blue-800 dark:bg-blue-950/20 dark:text-blue-300">
              <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
              <span>{t('tracker.printTechSwitch.labelStillSays', { tech: target.from })}</span>
            </div>
          )}

          <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-950/20 dark:text-amber-300">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            <span>{t('tracker.printTechSwitch.atPrinterWarning')}</span>
          </div>

          <div className="space-y-2">
            <label htmlFor="print-tech-switch-note" className="text-sm font-medium">
              {t('tracker.printTechSwitch.noteLabel')}
            </label>
            <Textarea
              id="print-tech-switch-note"
              placeholder={t('tracker.printTechSwitch.notePlaceholder')}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="min-h-[80px] text-base"
              maxLength={NOTE_MAX}
            />
            <div className="text-right text-[11px] text-muted-foreground">
              {note.length}/{NOTE_MAX}
            </div>
          </div>

          {refusal && (
            <div
              role="alert"
              className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800 dark:border-red-800 dark:bg-red-950/20 dark:text-red-300"
            >
              <XCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
              <span>{t(SWITCH_REFUSAL_KEYS[refusal], { tech: target.to })}</span>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" className="h-12" onClick={() => onOpenChange(false)} disabled={isPending}>
              {t('tracker.printTechSwitch.cancel')}
            </Button>
            <Button className="h-12" onClick={handleConfirm} disabled={isPending}>
              {isPending
                ? t('tracker.printTechSwitch.switching')
                : t('tracker.printTechSwitch.confirm', { tech: target.to })}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}
